'use client';

import React, { useState } from 'react';
import { CalendarDays, ChevronDown, RefreshCw, Clock } from 'lucide-react';

interface Props {
  activeEvent: string;
  onEventChange: (id: string) => void;
}

const events = [
  { id: 'nra-show-2026', label: 'NRA Show 2026', daysUntil: 18, status: 'active' },
  { id: 'foodex-2026', label: 'FOODEX Japan 2026', daysUntil: 54, status: 'planned' },
  { id: 'anuga-2026', label: 'Anuga 2026', daysUntil: 198, status: 'planned' },
];

export default function DashboardHeader({ activeEvent, onEventChange }: Props) {
  const [open, setOpen] = React.useState(false);
  const selected = events.find((e) => e.id === activeEvent) ?? events[0];

  return (
    <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-6 xl:px-8 2xl:px-10 py-4 max-w-screen-2xl mx-auto">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-foreground tracking-tight">Dashboard</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Pre-show outreach pipeline overview</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Event selector */}
          <div className="relative">
            <button
              onClick={() => setOpen(!open)}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors duration-150"
            >
              <CalendarDays size={15} className="text-primary" />
              <span>{selected.label}</span>
              <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold font-mono rounded bg-amber-100 text-amber-700">
                {selected.daysUntil}d
              </span>
              <ChevronDown size={13} className="text-muted-foreground" />
            </button>
            {open && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-border rounded-xl shadow-dropdown z-50 fade-in overflow-hidden">
                {events.map((ev) => (
                  <button
                    key={`event-sel-${ev.id}`}
                    onClick={() => { onEventChange(ev.id); setOpen(false); }}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-sm hover:bg-muted transition-colors ${ev.id === activeEvent ? 'bg-primary/5 text-primary font-medium' : 'text-foreground'}`}
                  >
                    <span>{ev.label}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{ev.daysUntil}d away</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Last updated */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock size={12} />
            <span>Updated 2 min ago</span>
          </div>
          <button className="btn-ghost p-2" aria-label="Refresh data">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>
      {/* Event countdown banner */}
      <div className="mt-3 flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-center gap-2">
          <CalendarDays size={14} className="text-amber-600" />
          <span className="text-xs font-semibold text-amber-800">NRA Show 2026</span>
          <span className="text-xs text-amber-700">· Chicago, IL · May 17–20, 2026</span>
        </div>
        <div className="ml-auto flex items-center gap-4 text-xs text-amber-700">
          <span><strong className="font-mono text-amber-900">18</strong> days until show opens</span>
          <span className="text-amber-400">|</span>
          <span><strong className="font-mono text-amber-900">8</strong> meetings confirmed</span>
          <span className="text-amber-400">|</span>
          <span><strong className="font-mono text-amber-900">12</strong> tasks overdue</span>
        </div>
      </div>
    </div>
  );
}